import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import LangCurrencySelector from "@/components/public/LangCurrencySelector";
import {
    ArrowRight, CheckCircle2, Users, Briefcase, Calculator, Home as HomeIcon,
    Sparkles, Wallet, TrendingUp, ShieldCheck, Repeat, Gauge, FileLock2,
    BellRing, LineChart, Landmark, Coins, CreditCard, Bell,
} from "lucide-react";

/* ═════════════════════════════════════════════════════════════════════
   Public Solutions pages — /solutions/:slug
   Segments: individuals · families · business · advisors
   ═════════════════════════════════════════════════════════════════════ */

const SOLUTIONS = {
    individuals: {
        eyebrow: "FOR INDIVIDUALS",
        title: "Complete personal financial control.",
        subtitle: "Cards, cash, income, subscriptions, warranties and net worth — all in one calm command centre.",
        icon: Users,
        accent: "emerald",
        heroBullets: [
            "One dashboard for every account, card and asset you own",
            "Never miss a card payment, renewal or document expiry",
            "See exactly where your money goes, month after month",
        ],
        outcomes: [
            { icon: Wallet,      title: "One clear picture", desc: "Combine every card, bank account, cash wallet and investment into a single live net-worth number." },
            { icon: BellRing,    title: "Nothing slips through", desc: "Alerts before due dates, before expiries, before your credit utilisation hurts your score." },
            { icon: Gauge,       title: "Understand where money goes", desc: "Category-wise spend, business vs personal split, and month-over-month deltas — no spreadsheets." },
            { icon: ShieldCheck, title: "Privacy-first by default", desc: "Masked card numbers, no CVV/OTP storage, encrypted document vault, full audit history." },
        ],
        modules: ["Credit Cards", "Expenses", "Bank Accounts", "Subscriptions", "Documents", "Investments", "Net Worth", "Renewals", "Rewards Tracker"],
        proof: [
            { stat: "40+", label: "cards tracked side by side" },
            { stat: "0", label: "CVV / OTP / PINs stored" },
            { stat: "1", label: "single monthly close" },
        ],
        cta: "Start your VAULT",
    },
    families: {
        eyebrow: "FOR FAMILIES",
        title: "One financial life, shared calmly.",
        subtitle: "Bring the household together — shared visibility, shared budgets, one net-worth number that actually reflects the family.",
        icon: HomeIcon,
        accent: "amber",
        heroBullets: [
            "Every family member's cards and spends in one household view",
            "Shared budgets by category, by person, by month",
            "Consolidated net worth across all household holdings",
        ],
        outcomes: [
            { icon: Users,       title: "Everyone on the same page", desc: "Add spouse, kids and parents as contributors; each contributes accounts to a shared household view." },
            { icon: Coins,       title: "Shared budgets & spending caps", desc: "Set caps by member, category or card. Vault alerts before someone crosses them." },
            { icon: FileLock2,   title: "Family document vault",       desc: "Insurance, warranties, school records, passports — encrypted and permission-scoped per member." },
            { icon: LineChart,   title: "Household net worth",         desc: "Roll-up of every account across every member, updated weekly with a clean delta trail." },
        ],
        modules: ["Family View", "Household Budgets", "Shared Documents", "Renewals", "Bills", "Insurance Tracker", "Kids' Milestones", "Family Health Score"],
        proof: [
            { stat: "5", label: "household members included on Family" },
            { stat: "100%", label: "role-based access — you decide who sees what" },
            { stat: "1", label: "consolidated net-worth number" },
        ],
        cta: "Set up your Family VAULT",
    },
    business: {
        eyebrow: "FOR BUSINESS OWNERS",
        title: "Separate personal and business without the mess.",
        subtitle: "Vault understands owner-operators. Tag every expense, every card, every income stream — and export a clean CA-ready pack anytime.",
        icon: Briefcase,
        accent: "sapphire",
        heroBullets: [
            "One-click personal vs business split on every transaction",
            "Cash Desk with Petty · Service · Personal accounts and monthly closing",
            "Auditor / CA-ready export bundle, on demand",
        ],
        outcomes: [
            { icon: Wallet,      title: "Personal vs business, always clear", desc: "Every expense carries a `business_personal` tag. Reports show both together — or filter to one." },
            { icon: Coins,       title: "Multi-account Cash Desk",           desc: "Petty, Service and Personal cash accounts with transfers, weekly reconciliation and monthly closing per account." },
            { icon: ClipboardCheck, title: "Reconciliation you'll actually trust", desc: "Statements ↔ payments matched, exceptions flagged, closing locks the month." },
            { icon: TrendingUp,  title: "Growth without the chaos",          desc: "Track vendor spend, category budgets, GST fields on every business transaction." },
        ],
        modules: ["Cash Desk", "Vendor Master", "GST Tagging", "Reconciliation", "Monthly Closing", "Payments", "Refunds / Reversals", "Statements", "CA-Ready Pack"],
        proof: [
            { stat: "7", label: "sub-modules inside Cash Desk" },
            { stat: "3", label: "cash accounts (Petty · Service · Personal), fully separable" },
            { stat: "1-click", label: "CA / auditor export bundle" },
        ],
        cta: "Set up your Business VAULT",
    },
    advisors: {
        eyebrow: "FOR ADVISORS & CHARTERED ACCOUNTANTS",
        title: "Consolidated, permission-based client reporting.",
        subtitle: "For financial advisors and CAs — one hand-off pack per client, with role-scoped read access and a clean audit trail.",
        icon: Calculator,
        accent: "violet",
        heroBullets: [
            "Read-only Auditor / CA View — no risk of accidental edits",
            "CA-Ready ZIP pack: expenses, statements, payments, subs, business/personal split",
            "White-label the report cover with your practice's name (Business plan)",
        ],
        outcomes: [
            { icon: ShieldCheck, title: "Permission-scoped access", desc: "Clients grant you a time-boxed, read-only view. Every access is audit-logged." },
            { icon: Repeat,      title: "One-click CA-Ready pack",   desc: "Excel bundle with expenses, statements, payments, subs and P/B split — perfect for a hand-off." },
            { icon: FileLock2,   title: "Encrypted document access", desc: "Only documents your client shares are visible. Password vault is second-lock protected." },
            { icon: LineChart,   title: "Trend & health reports",    desc: "Financial Health Score + monthly trend built-in — save hours per client at closing." },
        ],
        modules: ["Auditor / CA View", "CA-Ready Pack", "Shared Auditor Links", "Monthly Closing", "Tax Centre", "Reconciliation Log", "Audit Trail", "Statement Vault"],
        proof: [
            { stat: "100%", label: "read-only for CA-View sessions" },
            { stat: "1-click", label: ".zip export of the entire client's month" },
            { stat: "White-label", label: "cover page (Business plan)" },
        ],
        cta: "Invite a client to VAULT",
    },
};

// Alias the labeled URL slugs specified in the public footer/nav spec
// to the existing rich content entries so `/solutions/business-owners`,
// `/solutions/finance-teams` and `/solutions/accountants` all resolve.
SOLUTIONS["business-owners"] = SOLUTIONS.business;
SOLUTIONS["finance-teams"] = SOLUTIONS.business;
SOLUTIONS["accountants"] = SOLUTIONS.advisors;

// Small icon shim for a name we forgot to import above
function ClipboardCheck({ size = 20, strokeWidth = 1.6 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="4" rx="2" />
            <path d="M9 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
            <path d="m9 14 2 2 4-4" />
        </svg>
    );
}

// Public nav shared with Landing — kept minimal on purpose
const NAV_LINKS = [
    { label: "Product",   href: "/#pillars" },
    { label: "Solutions", href: "/#segments" },
    { label: "Security",  href: "/#security" },
    { label: "Pricing",   href: "/#pricing" },
    { label: "Resources", href: "/#how" },
];

import FeatureTestimonialWidget from "@/components/FeatureTestimonialWidget";
import BrandFooter from "@/components/BrandFooter";

const ACCENT_MAP = {
    emerald:  { chip: "bg-emerald-50 text-emerald-700",  dot: "bg-emerald-500",  ring: "ring-emerald-200" },
    amber:    { chip: "bg-amber-50 text-amber-700",      dot: "bg-amber-500",    ring: "ring-amber-200" },
    sapphire: { chip: "bg-blue-50 text-blue-700",        dot: "bg-blue-500",     ring: "ring-blue-200" },
    violet:   { chip: "bg-violet-50 text-violet-700",    dot: "bg-violet-500",   ring: "ring-violet-200" },
};

export default function Solutions() {
    const { slug } = useParams();
    const { user } = useAuth();
    const { t } = useLocale();  // ← was missing; `t(...)` calls below crashed the whole component
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!SOLUTIONS[slug]) return <Navigate to="/" replace />;
    // Send logged-in users into the dashboard — Solutions pages are for prospects
    if (user && user !== false) return <Navigate to="/dashboard" replace />;

    const s = SOLUTIONS[slug];
    const Icon = s.icon;
    const acc = ACCENT_MAP[s.accent] || ACCENT_MAP.emerald;

    return (
        <div className="landing-root" data-testid={`solutions-${slug}`} style={{ background: "#F6F3EC" }}>
            <style>{solutionsStyles}</style>

            {/* ═══════ NAV ═══════ */}
            <header className={`landing-nav ${scrolled ? "landing-nav--scrolled" : ""}`}>
                <div className="landing-container flex items-center justify-between py-4">
                    <Link to="/" className="flex items-baseline gap-2">
                        <span className="brand-word">VAULT</span>
                        <span className="brand-tag">by PD</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-7">
                        {NAV_LINKS.map((l) => (
                            <a key={l.href} href={l.href} className="landing-nav-link">{l.label}</a>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <LangCurrencySelector />
                        <Link to="/login" className="hidden sm:inline text-sm font-medium text-stone-700 hover:text-stone-950">{t("nav.sign_in")}</Link>
                        <Link to="/signup" className="btn-primary">{t("nav.start_free")}</Link>
                    </div>
                </div>
            </header>

            {/* ═══════ HERO ═══════ */}
            <section className="landing-container pt-14 md:pt-20 pb-16 md:pb-24">
                <div className="flex items-center gap-2 mb-6">
                    <Link to="/" className="text-[13px] text-stone-500 hover:text-stone-900">Home</Link>
                    <span className="text-stone-300">/</span>
                    <span className="text-[13px] text-stone-500">Solutions</span>
                    <span className="text-stone-300">/</span>
                    <span className="text-[13px] font-medium text-stone-900 capitalize">{slug}</span>
                </div>

                <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-center">
                    <div>
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${acc.chip} text-[10.5px] font-bold uppercase tracking-widest`}>
                            <Sparkles size={12} /> {s.eyebrow}
                        </div>
                        <h1 className="hero-headline mt-6" data-testid="solutions-headline">{t(`solutions.${slug}.title`) !== `solutions.${slug}.title` ? t(`solutions.${slug}.title`) : s.title}</h1>
                        <p className="hero-sub mt-6 max-w-xl">{t(`solutions.${slug}.subtitle`) !== `solutions.${slug}.subtitle` ? t(`solutions.${slug}.subtitle`) : s.subtitle}</p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/signup" className="btn-primary-lg" data-testid="solutions-cta">
                                {t(`solutions.${slug}.cta`) !== `solutions.${slug}.cta` ? t(`solutions.${slug}.cta`) : s.cta} <ArrowRight size={16} />
                            </Link>
                            <Link to="/login?demo=1" className="btn-ghost-lg">{t("hero.view_demo")}</Link>
                        </div>
                        <ul className="mt-8 space-y-2.5">
                            {s.heroBullets.map((b) => (
                                <li key={b} className="flex items-center gap-2 text-[13.5px] text-stone-700">
                                    <CheckCircle2 size={15} className="text-emerald-600 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={`p-8 rounded-3xl bg-white border border-stone-200 shadow-[0_30px_60px_-30px_rgba(11,30,63,0.25)] ring-1 ${acc.ring}`}>
                        <div className={`w-14 h-14 rounded-2xl ${acc.chip} flex items-center justify-center`}>
                            <Icon size={24} strokeWidth={1.7} />
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-4">
                            {s.proof.map((p) => (
                                <div key={p.label} className="text-left">
                                    <div className="text-2xl font-bold text-stone-950 tabular-nums">{p.stat}</div>
                                    <div className="text-[11px] text-stone-500 leading-snug mt-0.5">{p.label}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 pt-5 border-t border-stone-100">
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-3">Modules included</div>
                            <div className="flex flex-wrap gap-1.5">
                                {s.modules.map((m) => <span key={m} className="chip">{m}</span>)}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════ OUTCOMES ═══════ */}
            <section className="landing-container py-14 md:py-20">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-stone-500">Why it works for {slug}</div>
                    <h2 className="section-heading mt-3">Outcomes you can feel in a week.</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {s.outcomes.map((o) => {
                        const OIcon = o.icon;
                        return (
                            <div key={o.title} className="card-lg">
                                <div className={`w-11 h-11 rounded-xl ${acc.chip} flex items-center justify-center mb-4`}>
                                    <OIcon size={20} strokeWidth={1.7} />
                                </div>
                                <h3 className="text-lg font-bold text-stone-900">{o.title}</h3>
                                <p className="text-[14px] text-stone-600 mt-2 leading-relaxed">{o.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ═══════ SOCIAL PROOF — feature-specific testimonial (silent if none) ═══════ */}
            <section className="landing-container">
                <FeatureTestimonialWidget tag="money-map" />
            </section>

            {/* ═══════ BRAND FOOTER — pulled from central Social Identity registry ═══════ */}
            <section className="landing-container py-6" style={{ background: "#0B1E3F", color: "#F6F3EC", marginTop: 40 }}>
                <BrandFooter platform="LinkedIn" dense />
            </section>

            {/* ═══════ OTHER SOLUTIONS ═══════ */}
            <section className="landing-container py-14 md:py-20">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-stone-500">Also see</div>
                    <h2 className="section-heading mt-3">Vault is built for the way you already think about money.</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(SOLUTIONS).filter(([k]) => k !== slug).map(([k, other]) => {
                        const OIcon = other.icon;
                        const oacc = ACCENT_MAP[other.accent] || ACCENT_MAP.emerald;
                        return (
                            <Link
                                key={k}
                                to={`/solutions/${k}`}
                                className="card-md hover:shadow-md transition"
                                data-testid={`solutions-nav-${k}`}
                            >
                                <div className={`w-10 h-10 rounded-lg ${oacc.chip} flex items-center justify-center mb-3`}>
                                    <OIcon size={18} />
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{other.eyebrow}</div>
                                <div className="text-[14px] font-semibold text-stone-900 mt-1 capitalize">{k}</div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ═══════ FINAL CTA ═══════ */}
            <section className="py-14 md:py-24" style={{ background: "linear-gradient(135deg, #101827 0%, #0b1e3f 100%)" }}>
                <div className="landing-container text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight max-w-2xl mx-auto leading-tight">
                        Ready to bring it all together?
                    </h2>
                    <p className="text-white/70 mt-4 max-w-xl mx-auto text-[15px]">Start free, invite your household or team, and consolidate every account, card and document in minutes.</p>
                    <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
                        <Link to="/signup" className="btn-primary-lg">{s.cta} <ArrowRight size={16} /></Link>
                        <Link to="/login?demo=1" className="btn-ghost-lg btn-ghost-lg--dark">Explore Demo</Link>
                    </div>
                </div>
            </section>

            {/* ═══════ FOOTER ═══════ */}
            <footer className="landing-container py-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-baseline gap-2">
                            <span className="brand-word">VAULT</span>
                            <span className="brand-tag">by PD</span>
                        </div>
                        <div className="text-[12px] text-stone-500 mt-1">The Personal Financial Operating System · Created by PD</div>
                    </div>
                    <div className="flex items-center gap-6 text-[13px] text-stone-600 flex-wrap">
                        <Link to="/privacy" className="hover:text-stone-950">Privacy</Link>
                        <Link to="/terms" className="hover:text-stone-950">Terms</Link>
                        <Link to="/contact" className="hover:text-stone-950">Contact</Link>
                        <Link to="/login" className="hover:text-stone-950">Sign in</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

const solutionsStyles = `
.landing-root { font-family: "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; color: #1c1917; -webkit-font-smoothing: antialiased; }
.landing-container { max-width: 1200px; margin: 0 auto; padding-left: 24px; padding-right: 24px; }
.landing-nav { position: sticky; top: 0; z-index: 40; background: rgba(246, 243, 236, 0.85); backdrop-filter: blur(10px); transition: box-shadow .2s ease, border-color .2s ease; border-bottom: 1px solid transparent; }
.landing-nav--scrolled { border-bottom-color: rgba(0,0,0,0.06); }
.landing-nav-link { font-size: 14px; color: #44403c; font-weight: 500; }
.brand-word { font-weight: 900; font-size: 22px; letter-spacing: 0.22em; color: #0b1e3f; }
.brand-tag { font-weight: 600; font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: rgba(11,30,63,0.55); transform: translateY(-2px); }
.btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #0b1e3f; color: #fff; padding: 9px 16px; border-radius: 999px; font-size: 13px; font-weight: 600; }
.btn-primary:hover { background: #101827; }
.btn-primary-lg { display: inline-flex; align-items: center; gap: 8px; background: #0b1e3f; color: #fff; padding: 12px 22px; border-radius: 999px; font-size: 15px; font-weight: 600; box-shadow: 0 6px 20px -8px rgba(11,30,63,0.5); }
.btn-primary-lg:hover { background: #101827; transform: translateY(-1px); }
.btn-ghost-lg { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; font-size: 15px; font-weight: 600; border: 1px solid rgba(0,0,0,0.12); color: #1c1917; }
.btn-ghost-lg:hover { background: rgba(0,0,0,0.03); border-color: rgba(0,0,0,0.2); }
.btn-ghost-lg--dark { color: #fff; border-color: rgba(255,255,255,0.25); }
.btn-ghost-lg--dark:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.4); }
.hero-headline { font-size: 40px; font-weight: 800; line-height: 1.08; letter-spacing: -0.02em; color: #0b1e3f; }
@media (min-width: 768px) { .hero-headline { font-size: 56px; } }
.hero-sub { font-size: 17px; color: #57534E; line-height: 1.55; }
.section-heading { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; color: #0b1e3f; }
@media (min-width: 768px) { .section-heading { font-size: 38px; } }
.card-lg { background: #fff; border: 1px solid rgba(0,0,0,0.06); border-radius: 20px; padding: 24px; transition: transform .15s ease, box-shadow .15s ease; }
.card-lg:hover { transform: translateY(-2px); box-shadow: 0 22px 40px -20px rgba(11,30,63,0.15); }
.card-md { background: #fff; border: 1px solid rgba(0,0,0,0.06); border-radius: 16px; padding: 16px; display: block; }
.chip { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; background: #F5F5F4; color: #44403C; font-size: 11.5px; font-weight: 500; }
`;
